import { useCallback, useEffect, useState } from 'react';

import { SearchQuery } from '~/features/advanced-search/types';
import { StorageService } from '~/services/StorageService/StorageService';

const STORAGE_KEY = 'ticket-filters';

/**
 * Custom hook to persist ticket filters in localStorage
 * Filters persist across user sessions and all tabs
 */
export const usePersistedFilters = () => {
  const [filters, setFilters] = useState<SearchQuery>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  const storageKey = STORAGE_KEY;

  useEffect(() => {
    const stored = StorageService.getItem(storageKey);

    try {
      if (stored) {
        const parsedFilters = JSON.parse(stored);
        setFilters(parsedFilters);
      }
    } catch (error) {
      console.warn('Failed to load persisted filters:', error);
    } finally {
      setIsLoaded(true);
    }
  }, [storageKey]);

  // Save filters to localStorage whenever they change
  const updateFilters = useCallback(
    (newFilters: SearchQuery) => {
      setFilters(newFilters);
      StorageService.setItem(storageKey, JSON.stringify(newFilters));
    },
    [storageKey],
  );

  // Clear filters for current tab
  const clearFilters = useCallback(() => {
    setFilters([]);
    StorageService.removeItem(storageKey);
  }, [storageKey]);

  return {
    filters,
    updateFilters,
    clearFilters,
    isLoaded,
  };
};
